import { useEffect, useState } from 'react';
import { Tooltip } from '@odo/shared-ui';
import type { TaskBodyProps } from './index';
import { advanceWorkflow, type OutgoingAction } from '../../../services/workQueue';
import { useReviewActionRunner, type ActionProgress } from './useReviewActionRunner';
import { V2_LANE_ACTIONS } from '../constants';

const MAX_LEN = 10_000;
const BUTTON_CLASS =
    'px-4 py-2 rounded-md text-xs font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
const REJECT_BUTTON_CLASS = `${BUTTON_CLASS} border border-red-500 text-red-500 bg-white hover:bg-red-50`;

interface ProgressLabels {
    idle: string;
    busy: string;
    done: string;
}

function progressLabel(
    action: OutgoingAction | null | undefined,
    progress: ActionProgress,
    labels: ProgressLabels
): string {
    if (action && progress.succeeded === action.action) return labels.done;
    if (action && progress.pending === action.action) return labels.busy;
    return labels.idle;
}

type RunAction = ReturnType<typeof useReviewActionRunner>['runAction'];

interface RejectFormProps {
    caseId: string;
    action: OutgoingAction;
    progress: ActionProgress;
    runAction: RunAction;
    disabled: boolean;
    onCancel: () => void;
}

function rejectTextareaBorder(length: number): string {
    if (length > MAX_LEN) return 'border-red-300 focus:border-red-400';
    if (length > 0) return 'border-green-500 bg-green-50/40 focus:border-green-600';
    return 'border-gray-300 focus:border-[rgb(55,115,168)]';
}

function RejectForm({ caseId, action, progress, runAction, disabled, onCancel }: RejectFormProps) {
    const [text, setText] = useState('');
    const trimmed = text.trim();
    const length = trimmed.length;
    const canSend = length > 0 && length <= MAX_LEN;
    const send = (): void => {
        if (!canSend) return;
        void runAction(
            action,
            () => advanceWorkflow(caseId, action.action, { designPlan: trimmed }),
            'DesignReviewTask.handleReject',
            'Failed to submit instructions. Please try again.'
        );
    };
    return (
        <>
            <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-gray-700">
                    New instructions for the manufacturer
                </label>
                <span
                    className={`text-[11px] tabular-nums ${length > MAX_LEN ? 'text-red-500' : 'text-gray-400'}`}
                >
                    {length.toLocaleString()} / {MAX_LEN.toLocaleString()}
                </span>
            </div>
            <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Describe the changes the manufacturer needs to make"
                rows={4}
                disabled={disabled}
                spellCheck
                autoFocus
                className={`w-full px-3 py-2 border rounded-md text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-100 resize-none leading-relaxed disabled:opacity-50 ${rejectTextareaBorder(length)}`}
            />
            <div className="flex items-center justify-end gap-2">
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={disabled}
                    className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 text-xs font-bold bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={send}
                    disabled={disabled || !canSend}
                    className={`${BUTTON_CLASS} bg-red-600 text-white hover:bg-red-700`}
                >
                    {progressLabel(action, progress, {
                        idle: 'Send to manufacturer',
                        busy: 'Submitting…',
                        done: 'Sent ✓'
                    })}
                </button>
            </div>
        </>
    );
}

// Not direction-filtered: reject needs the backward edge; requiring payloadShape
// 'design-plan' keeps the lateral 'both' reactivate edge out.
function splitReviewActions(outgoing: OutgoingAction[]) {
    const internal = outgoing.filter(
        (a) => a.actor === 'internal' && !V2_LANE_ACTIONS.has(a.action)
    );
    const approves = internal.filter((a) => a.direction === 'forward');
    return {
        primaryApprove: approves[0] ?? null,
        secondaryApproves: approves.slice(1),
        rejectAction: internal.find(
            (a) => a.direction !== 'forward' && a.payloadShape === 'design-plan'
        )
    };
}

type DesignReviewFooterProps = Pick<
    TaskBodyProps,
    'statusMeta' | 'onPayloadChange' | 'onTaskComplete'
> & {
    caseId: string;
    primaryLabel?: string;
    /** Disables the primary approve button and explains why in a tooltip. */
    primaryDisabledHint?: string | null;
    /** Awaited before any action fires, e.g. to save pending edits. */
    beforeAction?: () => Promise<void>;
};

// Approve / reject footer of the design review steps; it owns the submit, so
// TaskSurface hides its own footer.
export function DesignReviewFooter({
    caseId,
    statusMeta,
    onPayloadChange,
    onTaskComplete,
    primaryLabel,
    primaryDisabledHint = null,
    beforeAction
}: DesignReviewFooterProps) {
    const [rejecting, setRejecting] = useState(false);
    const { progress, runAction } = useReviewActionRunner(caseId, onTaskComplete, beforeAction);

    useEffect(() => {
        onPayloadChange?.({ payload: null, valid: true, bodyOwnsSubmit: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The engine's first forward edge is the happy path (dark, rightmost); extra
    // forward edges (e.g. redesign's approve-with-Dx-unchanged) render outlined.
    const { primaryApprove, secondaryApproves, rejectAction } = splitReviewActions(
        statusMeta.outgoingActions
    );
    const anyBusy = progress.pending !== null || progress.succeeded !== null;

    const approve = (action: OutgoingAction): void => {
        void runAction(
            action,
            () => advanceWorkflow(caseId, action.action),
            'DesignReviewTask.handleApprove',
            'Failed to approve design. Please try again.'
        );
    };

    const approveLabels = (idle: string): ProgressLabels => ({
        idle,
        busy: 'Approving…',
        done: 'Approved ✓'
    });

    const primaryButton = (
        <button
            type="button"
            onClick={() => primaryApprove && approve(primaryApprove)}
            disabled={anyBusy || !primaryApprove || primaryDisabledHint !== null}
            className={`${BUTTON_CLASS} bg-gray-800 text-white hover:bg-gray-900`}
        >
            {progressLabel(
                primaryApprove,
                progress,
                approveLabels(primaryLabel ?? primaryApprove?.label ?? 'Approve')
            )}
        </button>
    );

    return (
        <div className="flex-shrink-0 border-t border-gray-200 px-4 py-3 flex flex-col gap-3">
            {rejecting && rejectAction ? (
                <RejectForm
                    caseId={caseId}
                    action={rejectAction}
                    progress={progress}
                    runAction={runAction}
                    disabled={anyBusy}
                    onCancel={() => setRejecting(false)}
                />
            ) : (
                <div className="flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={() => setRejecting(true)}
                        disabled={anyBusy || !rejectAction}
                        className={REJECT_BUTTON_CLASS}
                    >
                        Reject
                    </button>
                    {secondaryApproves.map((a) => (
                        <button
                            key={a.action}
                            type="button"
                            onClick={() => approve(a)}
                            disabled={anyBusy}
                            className={`${BUTTON_CLASS} border border-gray-800 text-gray-800 bg-white hover:bg-gray-100`}
                        >
                            {progressLabel(a, progress, approveLabels(a.label))}
                        </button>
                    ))}
                    {primaryDisabledHint ? (
                        <Tooltip content={primaryDisabledHint} side="top">
                            <span className="inline-block">{primaryButton}</span>
                        </Tooltip>
                    ) : (
                        primaryButton
                    )}
                </div>
            )}
        </div>
    );
}

type DesignRejectFooterProps = Pick<TaskBodyProps, 'onTaskComplete'> & {
    caseId: string;
    rejectAction: OutgoingAction;
    beforeAction?: () => Promise<void>;
};

// Just the reject-with-new-instructions control, for steps whose other actions
// stay in TaskSurface's footer.
export function DesignRejectFooter({
    caseId,
    rejectAction,
    onTaskComplete,
    beforeAction
}: DesignRejectFooterProps) {
    const [rejecting, setRejecting] = useState(false);
    const { progress, runAction } = useReviewActionRunner(caseId, onTaskComplete, beforeAction);
    const anyBusy = progress.pending !== null || progress.succeeded !== null;

    return (
        <div className="flex-shrink-0 border-t border-gray-200 px-4 py-3 flex flex-col gap-3">
            {rejecting ? (
                <RejectForm
                    caseId={caseId}
                    action={rejectAction}
                    progress={progress}
                    runAction={runAction}
                    disabled={anyBusy}
                    onCancel={() => setRejecting(false)}
                />
            ) : (
                <div className="flex items-center justify-end">
                    <button
                        type="button"
                        onClick={() => setRejecting(true)}
                        disabled={anyBusy}
                        className={REJECT_BUTTON_CLASS}
                    >
                        Reject
                    </button>
                </div>
            )}
        </div>
    );
}
